import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Package, Plus, Edit, Trash2, Link as LinkIcon, Unlink,
  AlertTriangle, Wrench, ArrowLeft, Search, Filter, ArrowRightLeft, Tag
} from 'lucide-react';
import Removals from './Removals';

const API = process.env.REACT_APP_BACKEND_URL;

const MEMORIAL_TYPES = [
  'Headstone',
  'Lawn Memorial',
  'Kerb Set',
  'Vase',
  'Plaque',
  'Tablet',
  'Cremation Memorial',
  'Pet Memorial',
  'Other'
];

const STONE_TYPES = [
  'Granite',
  'Marble',
  'Slate',
  'Sandstone',
  'Limestone',
  'Other'
];

const STONE_FINISHES = [
  'Polished',
  'Honed',
  'Rustic',
  'Other'
];

function Stock() {
  const { token, user } = useContext(AuthContext);
  // Only Set In Stone can list inventory items for marketplace sale.
  const canListForSale = user?.role === 'super_admin' || user?.company_id === 'set-in-stone';

  const [activeTab, setActiveTab] = useState('stock');
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAllocateDialog, setShowAllocateDialog] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAllocated, setFilterAllocated] = useState('all'); // all, allocated, available
  const [showTotalValue, setShowTotalValue] = useState(false);
  // List-for-sale: prefills the marketplace listing from a Stock inventory row.
  const [listForSaleDialog, setListForSaleDialog] = useState({ open: false, item: null });
  const [listForSaleForm, setListForSaleForm] = useState({
    title: '', description: '', price_each: 0, pallet_delivery_cost: 70,
    vat_rate: 20, collection_free: true, image_url: '', active: true
  });
  const [listingSubmitting, setListingSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    memorial_type: '',
    stone_code: '',
    stone_type: '',
    stone_colour: '',
    stone_finish: '',
    dimensions: { width: '', height: '', depth: '' },
    cost_price: '',
    selling_price: '',
    price_after_work: '',
    supplier: '',
    quantity: 1,
    is_damaged: false,
    requires_work: false,
    notes: ''
  });

  useEffect(() => {
    fetchStock();
    fetchOrders();
  }, [filterAllocated]);

  const fetchStock = async () => {
    try {
      let url = `${API}/api/memorial-stock`;
      if (filterAllocated === 'allocated') {
        url += '?allocated=true';
      } else if (filterAllocated === 'available') {
        url += '?allocated=false';
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStockItems(response.data);
    } catch (error) {
      toast.error('Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns {orders: [...], pagination: {...}}
      const ordersData = response.data.orders || response.data;
      // Filter to only show active orders (not archived)
      setOrders(ordersData.filter(o => !o.is_archived));
    } catch (error) {
      console.error('Failed to load orders');
    }
  };

  const resetForm = () => {
    setFormData({
      memorial_type: '',
      stone_code: '',
      stone_type: '',
      stone_colour: '',
      stone_finish: '',
      dimensions: { width: '', height: '', depth: '' },
      cost_price: '',
      selling_price: '',
      price_after_work: '',
      supplier: '',
      quantity: 1,
      is_damaged: false,
      requires_work: false,
      notes: ''
    });
  };

  const handleAddStock = async () => {
    try {
      const payload = {
        ...formData,
        dimensions: {
          width: formData.dimensions.width ? parseFloat(formData.dimensions.width) : null,
          height: formData.dimensions.height ? parseFloat(formData.dimensions.height) : null,
          depth: formData.dimensions.depth ? parseFloat(formData.dimensions.depth) : null
        },
        cost_price: parseFloat(formData.cost_price) || 0,
        selling_price: parseFloat(formData.selling_price) || 0,
        price_after_work: formData.price_after_work ? parseFloat(formData.price_after_work) : null,
        quantity: parseInt(formData.quantity) || 1
      };
      
      await axios.post(`${API}/api/memorial-stock`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stock item added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchStock();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to add stock item'); toast.error(msg);
    }
  };

  const handleEditStock = async () => {
    try {
      const payload = {
        ...formData,
        dimensions: {
          width: formData.dimensions.width ? parseFloat(formData.dimensions.width) : null,
          height: formData.dimensions.height ? parseFloat(formData.dimensions.height) : null,
          depth: formData.dimensions.depth ? parseFloat(formData.dimensions.depth) : null
        },
        cost_price: parseFloat(formData.cost_price) || 0,
        selling_price: parseFloat(formData.selling_price) || 0,
        price_after_work: formData.price_after_work ? parseFloat(formData.price_after_work) : null,
        quantity: parseInt(formData.quantity) || 1
      };
      
      await axios.put(`${API}/api/memorial-stock/${selectedStock.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stock item updated successfully');
      setShowEditDialog(false);
      setSelectedStock(null);
      resetForm();
      fetchStock();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update stock item'); toast.error(msg);
    }
  };

  const openListForSale = (item) => {
    const title = `${item.stone_colour} ${item.memorial_type}${item.stone_code ? ' — ' + item.stone_code : ''}`;
    setListForSaleForm({
      title,
      description: [item.stone_type, item.stone_finish, item.dimensions?.width && `${item.dimensions.width}×${item.dimensions.height}×${item.dimensions.depth || ''}`].filter(Boolean).join(' · '),
      price_each: Number(item.selling_price) || 0,
      pallet_delivery_cost: 70,
      vat_rate: 20,
      collection_free: true,
      image_url: '',
      active: true,
    });
    setListForSaleDialog({ open: true, item });
  };

  const submitListForSale = async () => {
    const item = listForSaleDialog.item;
    if (!item) return;
    if (!listForSaleForm.title.trim()) { toast.error('Title is required'); return; }
    setListingSubmitting(true);
    try {
      const payload = {
        source_stock_id: item.id,
        title: listForSaleForm.title.trim(),
        description: listForSaleForm.description,
        stone_colour: item.stone_colour,
        shape: item.memorial_type,
        finish: item.stone_finish || 'Polished',
        size: [item.dimensions?.width, item.dimensions?.height, item.dimensions?.depth].filter(Boolean).join(' × '),
        width_mm: Number(item.dimensions?.width) || null,
        height_mm: Number(item.dimensions?.height) || null,
        depth_mm: Number(item.dimensions?.depth) || null,
        qty_available: Number(item.quantity) || 0,
        price_each: Number(listForSaleForm.price_each) || 0,
        vat_rate: Number(listForSaleForm.vat_rate) || 20,
        pallet_delivery_cost: Number(listForSaleForm.pallet_delivery_cost) || 0,
        collection_free: !!listForSaleForm.collection_free,
        image_url: listForSaleForm.image_url || null,
        active: !!listForSaleForm.active,
      };
      await axios.post(`${API}/api/stock-memorials`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Listed "${payload.title}" on Group Ordering.`);
      setListForSaleDialog({ open: false, item: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to list stock');
    } finally {
      setListingSubmitting(false);
    }
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm('Are you sure you want to delete this stock item?')) return;
    
    try {
      await axios.delete(`${API}/api/memorial-stock/${stockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock item deleted');
      fetchStock();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete stock item'); toast.error(msg);
    }
  };

  const handleAllocate = async (orderId) => {
    try {
      await axios.post(`${API}/api/memorial-stock/${selectedStock.id}/allocate?order_id=${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock allocated to order');
      setShowAllocateDialog(false);
      setSelectedStock(null);
      fetchStock();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to allocate stock'); toast.error(msg);
    }
  };

  const handleDeallocate = async (stockId) => {
    if (!window.confirm('Remove this stock allocation from the order?')) return;
    
    try {
      await axios.post(`${API}/api/memorial-stock/${stockId}/deallocate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock deallocated from order');
      fetchStock();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to deallocate stock'); toast.error(msg);
    }
  };

  const openEditDialog = (stock) => {
    setSelectedStock(stock);
    setFormData({
      memorial_type: stock.memorial_type || '',
      stone_code: stock.stone_code || '',
      stone_type: stock.stone_type || '',
      stone_colour: stock.stone_colour || stock.stone_color || '',
      stone_finish: stock.stone_finish || '',
      dimensions: {
        width: stock.dimensions?.width || '',
        height: stock.dimensions?.height || '',
        depth: stock.dimensions?.depth || ''
      },
      cost_price: stock.cost_price || '',
      selling_price: stock.selling_price || '',
      price_after_work: stock.price_after_work || '',
      supplier: stock.supplier || '',
      quantity: stock.quantity || 1,
      is_damaged: stock.is_damaged || false,
      requires_work: stock.requires_work || false,
      notes: stock.notes || ''
    });
    setShowEditDialog(true);
  };

  const openAllocateDialog = (stock) => {
    setSelectedStock(stock);
    setShowAllocateDialog(true);
  };

  const filteredStock = stockItems.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.memorial_type?.toLowerCase().includes(search) ||
      item.stone_code?.toLowerCase().includes(search) ||
      item.stone_type?.toLowerCase().includes(search) ||
      (item.stone_colour || item.stone_color)?.toLowerCase().includes(search) ||
      item.supplier?.toLowerCase().includes(search) ||
      item.allocated_to_order_number?.toLowerCase().includes(search)
    );
  });

  // Calculate summary stats
  const totalItems = stockItems.length;
  const availableItems = stockItems.filter(s => !s.allocated_to_order_id).length;
  const allocatedItems = stockItems.filter(s => s.allocated_to_order_id).length;
  const damagedItems = stockItems.filter(s => s.is_damaged).length;

  // Calculate total stock value (selling price) and cost value
  const totalStockValue = stockItems.reduce((sum, item) => sum + (item.selling_price || 0), 0);
  const availableStockValue = stockItems.filter(s => !s.allocated_to_order_id).reduce((sum, item) => sum + (item.selling_price || 0), 0);
  const totalCostValue = stockItems.reduce((sum, item) => sum + (item.cost_price || 0), 0);
  const availableCostValue = stockItems.filter(s => !s.allocated_to_order_id).reduce((sum, item) => sum + (item.cost_price || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading stock...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Package className="w-7 h-7 text-blue-600" />
              Stock & Removals
            </h1>
            <p className="text-slate-600">Manage memorial inventory, removals, and allocations</p>
          </div>
        </div>
        {activeTab === 'stock' && (
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-stock-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Stock Item
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 shadow-sm border w-fit">
        <button
          onClick={() => setActiveTab('stock')}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'stock'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="stock-tab"
        >
          <Package className="w-4 h-4" />
          Memorial Stock
        </button>
        <button
          onClick={() => setActiveTab('removals')}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'removals'
              ? 'bg-orange-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="removals-tab"
        >
          <ArrowRightLeft className="w-4 h-4" />
          Removals
        </button>
      </div>

      {activeTab === 'removals' ? (
        <Removals />
      ) : (
      <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-800">{totalItems}</div>
            <div className="text-sm text-slate-600">Total Items</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{availableItems}</div>
            <div className="text-sm text-slate-600">Available</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{allocatedItems}</div>
            <div className="text-sm text-slate-600">Allocated</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">{damagedItems}</div>
            <div className="text-sm text-slate-600">Damaged/Needs Work</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by code, type, stone, color, supplier, order..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="stock-search"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterAllocated === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterAllocated('all')}
                size="sm"
              >
                All
              </Button>
              <Button
                variant={filterAllocated === 'available' ? 'default' : 'outline'}
                onClick={() => setFilterAllocated('available')}
                size="sm"
                className={filterAllocated === 'available' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                Available
              </Button>
              <Button
                variant={filterAllocated === 'allocated' ? 'default' : 'outline'}
                onClick={() => setFilterAllocated('allocated')}
                size="sm"
                className={filterAllocated === 'allocated' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                Allocated
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stock List */}
      <Card>
        <CardContent className="p-0">
          {filteredStock.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No stock items found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { resetForm(); setShowAddDialog(true); }}
              >
                Add your first stock item
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Stock Ref</th>
                    <th className="text-left p-4 font-medium text-slate-600">Type</th>
                    <th className="text-left p-4 font-medium text-slate-600">Stone</th>
                    <th className="text-left p-4 font-medium text-slate-600">Dimensions</th>
                    <th className="text-left p-4 font-medium text-slate-600">Price</th>
                    <th className="text-left p-4 font-medium text-slate-600">Status</th>
                    <th className="text-left p-4 font-medium text-slate-600">Allocation</th>
                    <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredStock.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50" data-testid={`stock-row-${item.id}`}>
                      <td className="p-4">
                        <span className="font-mono text-sm font-bold bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {item.stock_ref || 'N/A'}
                        </span>
                        {item.stone_code && (
                          <div className="text-xs text-slate-500 mt-1">{item.stone_code}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-slate-800">{item.memorial_type}</div>
                        {item.supplier && (
                          <div className="text-sm text-slate-500">{item.supplier}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-slate-800">{item.stone_type}</div>
                        <div className="text-sm text-slate-500">{item.stone_colour || item.stone_color}</div>
                        {item.stone_finish && (
                          <div className="text-xs text-slate-400">{item.stone_finish}</div>
                        )}
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {item.dimensions?.width || item.dimensions?.height ? (
                          <>
                            {item.dimensions.width && `W: ${item.dimensions.width}"`}
                            {item.dimensions.width && item.dimensions.height && ' × '}
                            {item.dimensions.height && `H: ${item.dimensions.height}"`}
                            {item.dimensions.depth && ` × D: ${item.dimensions.depth}"`}
                          </>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-slate-800">£{item.selling_price?.toFixed(2) || '0.00'}</div>
                        <div className="text-xs text-slate-500">Cost: £{item.cost_price?.toFixed(2) || '0.00'}</div>
                        {item.requires_work && item.price_after_work && (
                          <div className="text-xs text-amber-600">After work: £{item.price_after_work?.toFixed(2)}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {item.is_damaged && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Damaged
                            </Badge>
                          )}
                          {item.requires_work && (
                            <Badge variant="warning" className="text-xs bg-amber-100 text-amber-800">
                              <Wrench className="w-3 h-3 mr-1" />
                              Needs Work
                            </Badge>
                          )}
                          {!item.is_damaged && !item.requires_work && (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                              Ready
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {item.allocated_to_order_id ? (
                          <Link 
                            to={`/orders/${item.allocated_to_order_id}`}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          >
                            <LinkIcon className="w-3 h-3" />
                            Order #{item.allocated_to_order_number}
                          </Link>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            Available
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {!item.allocated_to_order_id ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openAllocateDialog(item)}
                              title="Allocate to order"
                            >
                              <LinkIcon className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeallocate(item.id)}
                              title="Remove allocation"
                              className="text-amber-600 hover:text-amber-700"
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(item)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {canListForSale && !item.allocated_to_order_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openListForSale(item)}
                              className="text-blue-700 hover:text-blue-800 border-blue-300"
                              title="List this item for sale on Group Ordering"
                              data-testid={`list-for-sale-${item.id}`}
                            >
                              <Tag className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteStock(item.id)}
                            className="text-red-600 hover:text-red-700"
                            disabled={!!item.allocated_to_order_id}
                            title={item.allocated_to_order_id ? 'Deallocate first' : 'Delete'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Total Stock Value Button - positioned to the left to avoid chat widget */}
      <div className="mt-6 flex justify-start">
        <div className="flex items-center gap-4">
          <Button
            variant={showTotalValue ? "default" : "outline"}
            onClick={() => setShowTotalValue(!showTotalValue)}
            data-testid="total-value-btn"
          >
            {showTotalValue ? 'Hide Values' : 'Show Stock Values'}
          </Button>
          {showTotalValue && (
            <div className="flex gap-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="text-left">
                <div className="text-sm text-slate-600">Total Cost Value</div>
                <div className="text-2xl font-bold text-red-600">£{totalCostValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-slate-500">
                  Available: £{availableCostValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-left border-l pl-6">
                <div className="text-sm text-slate-600">Total Selling Value</div>
                <div className="text-2xl font-bold text-green-600">£{totalStockValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-slate-500">
                  Available: £{availableStockValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-left border-l pl-6">
                <div className="text-sm text-slate-600">Potential Profit</div>
                <div className="text-2xl font-bold text-blue-600">£{(totalStockValue - totalCostValue).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-slate-500">
                  Margin: {totalCostValue > 0 ? (((totalStockValue - totalCostValue) / totalCostValue) * 100).toFixed(1) : 0}%
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Stock Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Stock Item</DialogTitle>
          </DialogHeader>
          <StockForm 
            formData={formData} 
            setFormData={setFormData}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStock} className="bg-blue-600 hover:bg-blue-700">
              Add Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Stock Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stock Item</DialogTitle>
          </DialogHeader>
          <StockForm 
            formData={formData} 
            setFormData={setFormData}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditStock} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Stock Dialog */}
      <Dialog open={showAllocateDialog} onOpenChange={setShowAllocateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Allocate Stock to Order</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Select an order to allocate this <strong>{selectedStock?.memorial_type}</strong> ({selectedStock?.stone_type} - {selectedStock?.stone_colour || selectedStock?.stone_color})
            </p>
            
            {/* Sort orders: matching first, then by order number */}
            {(() => {
              const stockCode = (selectedStock?.stone_code || '').toLowerCase();
              const stockColour = (selectedStock?.stone_colour || selectedStock?.stone_color || '').toLowerCase();
              
              // Score orders by match quality
              const scoredOrders = orders.map(order => {
                let score = 0;
                const orderCode = (order.stone_code || '').toLowerCase();
                const orderColour = (order.stone_colour || '').toLowerCase();
                
                // Exact code match = highest priority
                if (stockCode && orderCode && stockCode === orderCode) score += 100;
                else if (stockCode && orderCode && (stockCode.includes(orderCode) || orderCode.includes(stockCode))) score += 50;
                
                // Colour match
                if (stockColour && orderColour && stockColour === orderColour) score += 30;
                else if (stockColour && orderColour && (stockColour.includes(orderColour) || orderColour.includes(stockColour))) score += 15;
                
                return { ...order, matchScore: score };
              });
              
              // Sort by match score desc, then by order_number desc
              scoredOrders.sort((a, b) => {
                if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
                return (b.order_number || '').localeCompare(a.order_number || '');
              });
              
              const matchingOrders = scoredOrders.filter(o => o.matchScore > 0);
              const otherOrders = scoredOrders.filter(o => o.matchScore === 0);
              
              return (
                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  {/* Matching Orders Section */}
                  {matchingOrders.length > 0 && (
                    <>
                      <div className="bg-green-50 px-3 py-2 text-sm font-medium text-green-800 border-b sticky top-0">
                        Matching Orders ({matchingOrders.length})
                      </div>
                      {matchingOrders.map((order) => (
                        <div
                          key={order.id}
                          className="flex items-center justify-between p-3 border-b bg-green-50/50 hover:bg-green-100 cursor-pointer"
                          onClick={() => handleAllocate(order.id)}
                        >
                          <div className="flex-1">
                            <div className="font-medium flex items-center gap-2">
                              #{order.order_number} - {order.client_name}
                              <Badge className="bg-green-100 text-green-800 text-xs">Match</Badge>
                            </div>
                            <div className="text-sm text-slate-600">
                              {order.stone_code && <span className="font-mono bg-slate-100 px-1 rounded mr-2">{order.stone_code}</span>}
                              {order.stone_type} - {order.stone_colour}
                            </div>
                          </div>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700">
                            Allocate
                          </Button>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {/* All Other Orders Section */}
                  {otherOrders.length > 0 && (
                    <>
                      <div className="bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600 border-b sticky top-0">
                        All Orders ({otherOrders.length})
                      </div>
                      {otherOrders.map((order) => (
                        <div
                          key={order.id}
                          className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer"
                          onClick={() => handleAllocate(order.id)}
                        >
                          <div className="flex-1">
                            <div className="font-medium">#{order.order_number} - {order.client_name}</div>
                            <div className="text-sm text-slate-500">
                              {order.stone_code && <span className="font-mono bg-slate-100 px-1 rounded mr-2">{order.stone_code}</span>}
                              {order.stone_type} - {order.stone_colour}
                            </div>
                          </div>
                          <Button size="sm" variant="outline">
                            Allocate
                          </Button>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {orders.length === 0 && (
                    <div className="p-4 text-center text-slate-500">
                      No orders available
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocateDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* List for Sale dialog — pre-fills marketplace listing from inventory row */}
      <Dialog open={listForSaleDialog.open} onOpenChange={(o) => !o && setListForSaleDialog({ open: false, item: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-600" />
              List for Sale on Group Ordering
            </DialogTitle>
          </DialogHeader>
          {listForSaleDialog.item && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-2 text-xs text-slate-600">
                Pre-filled from your inventory item <strong>{listForSaleDialog.item.stone_colour} {listForSaleDialog.item.memorial_type}</strong>
                {listForSaleDialog.item.stock_ref && ` (ref ${listForSaleDialog.item.stock_ref})`}
                . Adjust price + delivery, then publish.
              </div>
              <div>
                <Label>Title *</Label>
                <Input value={listForSaleForm.title} onChange={(e) => setListForSaleForm({ ...listForSaleForm, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={listForSaleForm.description} onChange={(e) => setListForSaleForm({ ...listForSaleForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Price each £</Label>
                  <Input type="number" step="0.01" value={listForSaleForm.price_each} onChange={(e) => setListForSaleForm({ ...listForSaleForm, price_each: e.target.value })} />
                </div>
                <div>
                  <Label>VAT %</Label>
                  <Input type="number" step="0.1" value={listForSaleForm.vat_rate} onChange={(e) => setListForSaleForm({ ...listForSaleForm, vat_rate: e.target.value })} />
                </div>
                <div>
                  <Label>Pallet £</Label>
                  <Input type="number" step="0.01" value={listForSaleForm.pallet_delivery_cost} onChange={(e) => setListForSaleForm({ ...listForSaleForm, pallet_delivery_cost: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input placeholder="https://…" value={listForSaleForm.image_url} onChange={(e) => setListForSaleForm({ ...listForSaleForm, image_url: e.target.value })} />
              </div>
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={listForSaleForm.collection_free} onChange={(e) => setListForSaleForm({ ...listForSaleForm, collection_free: e.target.checked })} />
                  <span>Free collection available</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={listForSaleForm.active} onChange={(e) => setListForSaleForm({ ...listForSaleForm, active: e.target.checked })} />
                  <span>Active (visible immediately)</span>
                </label>
              </div>
              <div className="text-xs text-slate-500">
                Available quantity ({listForSaleDialog.item.quantity}) is pulled from this inventory row.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setListForSaleDialog({ open: false, item: null })}>Cancel</Button>
            <Button onClick={submitListForSale} disabled={listingSubmitting} className="bg-blue-600 hover:bg-blue-700" data-testid="list-for-sale-submit">
              {listingSubmitting ? 'Publishing…' : 'Publish on Group Ordering'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stock Form Component
function StockForm({ formData, setFormData }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
      <div>
        <Label>Memorial Type *</Label>
        <Select 
          value={formData.memorial_type} 
          onValueChange={(v) => setFormData({...formData, memorial_type: v})}
        >
          <SelectTrigger data-testid="memorial-type-select">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {MEMORIAL_TYPES.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Stone Code</Label>
        <Input
          value={formData.stone_code}
          onChange={(e) => setFormData({...formData, stone_code: e.target.value})}
          placeholder="e.g., OG123, BG456"
          data-testid="stone-code-input"
        />
        <p className="text-xs text-slate-500 mt-1">Used for matching orders to stock</p>
      </div>

      <div>
        <Label>Stone Type *</Label>
        <Select 
          value={formData.stone_type} 
          onValueChange={(v) => setFormData({...formData, stone_type: v})}
        >
          <SelectTrigger data-testid="stone-type-select">
            <SelectValue placeholder="Select stone type" />
          </SelectTrigger>
          <SelectContent>
            {STONE_TYPES.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Stone Colour *</Label>
        <Input
          value={formData.stone_colour}
          onChange={(e) => setFormData({...formData, stone_colour: e.target.value})}
          placeholder="e.g., Black, Grey, White"
          data-testid="stone-colour-input"
        />
      </div>

      <div>
        <Label>Stone Finish</Label>
        <Select 
          value={formData.stone_finish} 
          onValueChange={(v) => setFormData({...formData, stone_finish: v})}
        >
          <SelectTrigger data-testid="stone-finish-select">
            <SelectValue placeholder="Select finish" />
          </SelectTrigger>
          <SelectContent>
            {STONE_FINISHES.map(finish => (
              <SelectItem key={finish} value={finish}>{finish}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Supplier</Label>
        <Input
          value={formData.supplier}
          onChange={(e) => setFormData({...formData, supplier: e.target.value})}
          placeholder="Supplier name"
        />
      </div>

      <div className="md:col-span-2">
        <Label>Dimensions (inches)</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          <div>
            <Input
              type="number"
              placeholder="Width"
              value={formData.dimensions.width}
              onChange={(e) => setFormData({
                ...formData, 
                dimensions: {...formData.dimensions, width: e.target.value}
              })}
            />
          </div>
          <div>
            <Input
              type="number"
              placeholder="Height"
              value={formData.dimensions.height}
              onChange={(e) => setFormData({
                ...formData, 
                dimensions: {...formData.dimensions, height: e.target.value}
              })}
            />
          </div>
          <div>
            <Input
              type="number"
              placeholder="Depth"
              value={formData.dimensions.depth}
              onChange={(e) => setFormData({
                ...formData, 
                dimensions: {...formData.dimensions, depth: e.target.value}
              })}
            />
          </div>
        </div>
      </div>

      <div>
        <Label>Cost Price (£)</Label>
        <Input
          type="number"
          step="0.01"
          value={formData.cost_price}
          onChange={(e) => setFormData({...formData, cost_price: e.target.value})}
          placeholder="0.00"
        />
      </div>

      <div>
        <Label>Selling Price (£)</Label>
        <Input
          type="number"
          step="0.01"
          value={formData.selling_price}
          onChange={(e) => setFormData({...formData, selling_price: e.target.value})}
          placeholder="0.00"
        />
      </div>

      <div>
        <Label>Quantity</Label>
        <Input
          type="number"
          min="1"
          value={formData.quantity}
          onChange={(e) => setFormData({...formData, quantity: e.target.value})}
        />
      </div>

      <div className="flex items-center gap-6 pt-6">
        <div className="flex items-center gap-2">
          <Checkbox
            id="is_damaged"
            checked={formData.is_damaged}
            onCheckedChange={(checked) => setFormData({...formData, is_damaged: checked})}
          />
          <Label htmlFor="is_damaged" className="text-sm cursor-pointer flex items-center gap-1">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Damaged
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="requires_work"
            checked={formData.requires_work}
            onCheckedChange={(checked) => setFormData({...formData, requires_work: checked, price_after_work: checked ? formData.price_after_work : ''})}
          />
          <Label htmlFor="requires_work" className="text-sm cursor-pointer flex items-center gap-1">
            <Wrench className="w-4 h-4 text-amber-500" />
            Requires Work
          </Label>
        </div>
      </div>

      {/* Price After Work - only shown when requires_work is checked */}
      {formData.requires_work && (
        <div className="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <Label className="flex items-center gap-2 text-amber-800">
            <Wrench className="w-4 h-4" />
            Price After Work Completed (£)
          </Label>
          <Input
            type="number"
            step="0.01"
            value={formData.price_after_work}
            onChange={(e) => setFormData({...formData, price_after_work: e.target.value})}
            placeholder="0.00"
            className="mt-2"
          />
          <p className="text-xs text-amber-700 mt-1">
            Current selling price (£{formData.selling_price || '0.00'}) is used in stock totals. This is the value after work is done.
          </p>
        </div>
      )}

      <div className="md:col-span-2">
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Any additional notes about this stock item..."
          rows={3}
        />
      </div>
    </div>
  );
}

export default Stock;
